import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Star,
  Trash2,
  FileText,
  Clock,
  CheckCircle2,
  Target,
} from "lucide-react";
import { api } from "../lib/api";
import type { AssignmentTemplate } from "../lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

interface AssignmentTemplateManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectTemplate?: (template: AssignmentTemplate) => void;
}

export function AssignmentTemplateManager({
  open,
  onOpenChange,
  onSelectTemplate,
}: AssignmentTemplateManagerProps) {
  const [templates, setTemplates] = useState<AssignmentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] =
    useState<AssignmentTemplate | null>(null);

  useEffect(() => {
    if (open) {
      loadTemplates();
    }
  }, [open]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await api.getAssignmentTemplates();
      setTemplates(data.templates);
    } catch (error: any) {
      console.error("Error loading templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async (template: AssignmentTemplate) => {
    try {
      await api.toggleTemplateFavorite(template.id, !template.is_favorite);
      await loadTemplates();
    } catch (error: any) {
      console.error("Error toggling favorite:", error);
    }
  };

  const handleDeleteClick = (template: AssignmentTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    try {
      await api.deleteAssignmentTemplate(templateToDelete.id);
      await loadTemplates();
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (error: any) {
      console.error("Error deleting template:", error);
      alert("Failed to delete template");
    }
  };

  const handleSelectTemplate = (template: AssignmentTemplate) => {
    if (onSelectTemplate) {
      onSelectTemplate(template);
      onOpenChange(false);
    }
  };

  const getAssignmentTypeLabel = (type: string) => {
    switch (type) {
      case "technique_practice":
        return "Technique Practice";
      case "lesson_completion":
        return "Lesson Completion";
      case "problem_set":
        return "Problem Set";
      case "timed_challenge":
        return "Timed Challenge";
      case "accuracy_challenge":
        return "Accuracy Challenge";
      default:
        return type;
    }
  };

  const getAssignmentTypeIcon = (type: string) => {
    switch (type) {
      case "technique_practice":
        return <Target className="w-4 h-4" />;
      case "timed_challenge":
        return <Clock className="w-4 h-4" />;
      case "accuracy_challenge":
        return <CheckCircle2 className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assignment Templates</DialogTitle>
            <DialogDescription>
              Manage and reuse your assignment templates
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="text-center py-12 text-muted-foreground">
              Loading templates...
            </div>
          )}

          {!loading && templates.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No templates yet</p>
              <p className="text-sm mt-2">
                Create assignments and save them as templates for future use
              </p>
            </div>
          )}

          {!loading && templates.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card
                  key={template.id}
                  className="hover:border-primary transition-colors"
                >
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base truncate">
                          {template.name}
                        </CardTitle>
                        <CardDescription className="text-xs mt-1">
                          {template.description || "No description"}
                        </CardDescription>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 shrink-0"
                        onClick={() => handleToggleFavorite(template)}
                      >
                        <Star
                          className={`w-4 h-4 ${
                            template.is_favorite
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-muted-foreground"
                          }`}
                        />
                      </Button>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2">
                      {getAssignmentTypeIcon(template.assignment_type)}
                      <Badge variant="outline" className="text-xs">
                        {getAssignmentTypeLabel(template.assignment_type)}
                      </Badge>
                    </div>

                    <div className="space-y-1 text-xs text-muted-foreground">
                      <div className="flex justify-between">
                        <span>Points:</span>
                        <span className="font-medium">
                          {template.points_possible}
                        </span>
                      </div>
                      {template.suggested_due_days && (
                        <div className="flex justify-between">
                          <span>Suggested duration:</span>
                          <span className="font-medium">
                            {template.suggested_due_days} days
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Used:</span>
                        <span className="font-medium">
                          {template.usage_count} times
                        </span>
                      </div>
                      {template.auto_grade && (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle2 className="w-3 h-3" />
                          <span>Auto-graded</span>
                        </div>
                      )}
                    </div>
                  </CardContent>

                  <CardFooter className="flex gap-2">
                    {onSelectTemplate && (
                      <Button
                        variant="default"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleSelectTemplate(template)}
                      >
                        Use Template
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-9 p-0"
                      onClick={() => handleDeleteClick(template)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
